import React from 'react';
import { Theme, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CS_WEIGHT, csTokens, speakerTone } from 'components/CallScribe/callScribeTheme';
import { initialsFromName } from 'components/CallScribe/Recents/recentsHelpers';
import { mixHex, tint } from 'components/CallScribe/ui/color';

export type TAvatarSurface = 'paper' | 'stage';
export type TAvatarSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZES: Record<TAvatarSize, { box: number; font: number; icon: number }> = {
  sm: { box: 34, font: 12, icon: 18 },
  md: { box: 40, font: 14, icon: 20 },
  lg: { box: 48, font: 16, icon: 22 },
  xl: { box: 72, font: 24, icon: 30 },
};

export const avatarColors = (
  theme: Theme,
  tone: number | null,
  surface: TAvatarSurface,
): { fg: string; bg: string } => {
  const t = csTokens(theme);
  if (tone === null) {
    return surface === 'stage'
      ? { bg: mixHex('#FFFFFF', t.ground, 0.1), fg: t.onGround }
      : { bg: t.surface3, fg: t.text2 };
  }
  const s = speakerTone(theme, tone);
  if (surface === 'stage') {
    return { bg: mixHex(s.stageSolid, t.ground, 0.24), fg: t.onGround };
  }
  const dark = theme.palette.mode === 'dark';
  return {
    bg: tint(s.paperSolid, t.surface, dark ? 0.18 : 0.12),
    fg: dark ? s.paperSolid : mixHex(s.paperSolid, t.text, 0.7),
  };
};

const useAvatarStyles = makeStyles({
  avatar: {
    display: 'inline-grid',
    placeItems: 'center',
    flex: '0 0 auto',
    borderRadius: '50%',
    fontWeight: CS_WEIGHT.heavy,
    letterSpacing: 0,
    userSelect: 'none',
    '& svg': { display: 'block' },
  },
  initials: {
    fontSize: 'inherit',
    lineHeight: 1,
    fontWeight: 'inherit',
  },
});

export interface IAvatarProps {
  name: string;
  tone: number | null;
  surface?: TAvatarSurface;
  size?: TAvatarSize;
  icon?: React.ReactElement;
  label?: string;
}

const Avatar: React.FC<IAvatarProps> = ({
  name,
  tone,
  surface = 'paper',
  size = 'md',
  icon,
  label,
}) => {
  const classes = useAvatarStyles();
  const theme = useTheme();
  const { fg, bg } = avatarColors(theme, tone, surface);
  const dims = SIZES[size];
  const initials = /\p{L}/u.test(name) ? initialsFromName(name) : '';
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  return (
    <span
      data-avatar
      className={classes.avatar}
      style={{ width: dims.box, height: dims.box, fontSize: dims.font, color: fg, background: bg }}
      {...a11y}
    >
      {initials ? (
        <span className={classes.initials} aria-hidden={label ? true : undefined}>
          {initials}
        </span>
      ) : icon ? (
        React.cloneElement(icon, { 'aria-hidden': true, style: { fontSize: dims.icon } })
      ) : null}
    </span>
  );
};

export default Avatar;
